import React, { useMemo, useRef, useEffect } from "react";
import { Box } from "@mui/material";
import {
  DataGrid,
  GridFooterContainer,
  useGridApiContext,
  useGridSelector,
  gridVisibleColumnDefinitionsSelector,
} from "@mui/x-data-grid";
import { CustomNoRowsOverlay } from "../../../../components/common/OverlayGrid";
import { commonDataGridSx } from "../../../../components/common/commonDataGridStyles";
import { useTheme } from "../../../../ThemeContext";

const InProgressStickyFooter = ({ totals, hasData }) => {
  const { isDarkMode } = useTheme();
  const apiRef = useGridApiContext();
  const visibleColumns = useGridSelector(
    apiRef,
    gridVisibleColumnDefinitionsSelector
  );
  const scrollRef = useRef(null);

  useEffect(() => {
    const rootEl = apiRef.current?.rootElementRef?.current;
    if (!rootEl) return;
    const virtualScroller = rootEl.querySelector(
      ".MuiDataGrid-virtualScroller"
    );
    if (!virtualScroller) return;

    const handleScroll = () => {
      if (scrollRef.current) {
        scrollRef.current.scrollLeft = virtualScroller.scrollLeft;
      }
    };

    virtualScroller.addEventListener("scroll", handleScroll, {
      passive: true,
    });
    return () => {
      virtualScroller.removeEventListener("scroll", handleScroll);
    };
  }, [apiRef]);

  if (!hasData || !visibleColumns || visibleColumns.length === 0) return null;

  return (
    <GridFooterContainer
      sx={{
        backgroundColor: isDarkMode ? "#1e1e1e" : "#fff",
        borderTop: isDarkMode ? "1px solid #333" : "2px solid #e0e0e0",
        minHeight: "48px",
        height: "48px",
        p: 0,
        m: 0,
        width: "100%",
        overflow: "hidden",
      }}
    >
      <Box
        ref={scrollRef}
        sx={{
          display: "flex",
          width: "100%",
          overflowX: "hidden",
          alignItems: "center",
          height: "48px",
        }}
      >
        {visibleColumns.map((col) => {
          let content = null;
          let color = isDarkMode ? "#f1f5f9" : "#1e293b";
          let fontWeight = 700;

          if (col.field === "project") {
            content = "Total provisional";
          } else if (col.field === "model") {
            content = "";
          } else if (col.field === "hours") {
            content = totals?.hours || "0.00";
          } else if (col.field === "cost") {
            content = totals?.cost || "0.00";
          } else if (col.field === "project_cost") {
            content = totals?.project_cost || "—";
            color = isDarkMode ? "#94a3b8" : "#64748b";
            fontWeight = 500;
          } else if (col.field === "share") {
            content = "—";
            color = isDarkMode ? "#94a3b8" : "#94a3b8";
            fontWeight = 500;
          } else if (col.field === "earning") {
            content = totals?.earning || "0.00";
            color = isDarkMode ? "#4ade80" : "#16a34a";
          } else if (col.field === "status") {
            content = "";
          }

          return (
            <Box
              key={col.field}
              sx={{
                width: col.computedWidth,
                minWidth: col.computedWidth,
                maxWidth: col.computedWidth,
                px: "10px",
                boxSizing: "border-box",
                display: "flex",
                alignItems: "center",
                justifyContent:
                  col.align === "right" ? "flex-end" : "flex-start",
                fontSize: "13px",
                fontWeight,
                color,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {content}
            </Box>
          );
        })}
      </Box>
    </GridFooterContainer>
  );
};

const InProgressProvisional = ({
  selectedUserId,
  inProgress = [],
  isLoading = false,
  isDarkMode = false,
}) => {
  const columns = useMemo(
    () => [
      {
        field: "project",
        headerName: "Project",
        flex: 1.8,
        minWidth: 180,
        renderCell: (params) => (
          <span
            style={{
              fontWeight: 500,
              color: "#1e293b",
              fontSize: "13px",
            }}
          >
            {params.value}
          </span>
        ),
      },
      {
        field: "model",
        headerName: "Model",
        flex: 0.8,
        minWidth: 90,
        renderCell: (params) => {
          const modelType = params.row.budget_type || params.value;
          if (modelType === "FIXED_COST" || modelType === "Fixed" || modelType === "Fixed Cost") {
            return <span className="badge-model-fixed">Fixed Cost</span>;
          }
          if (modelType === "TIME_AND_MATERIAL" || modelType === "T&M(Contract)") {
            return <span className="badge-model-contract">T&M(Contract)</span>;
          }
          if (modelType === "CONTRACT" || modelType === "Contract") {
            return <span className="badge-model-contract">Contract</span>;
          }
          return (
            <span style={{ color: "#9ca3af", fontSize: "13px" }}>
              {params.value || "Fixed Cost"}
            </span>
          );
        },
      },
      {
        field: "hours",
        headerName: "Hours to date",
        flex: 1.0,
        minWidth: 110,
        headerAlign: "right",
        align: "right",
        renderCell: (params) => (
          <span style={{ color: "#374151", fontSize: "13px" }}>
            {params.value}
          </span>
        ),
      },
      {
        field: "cost",
        headerName: "Cost to date",
        flex: 1.1,
        minWidth: 120,
        headerAlign: "right",
        align: "right",
        renderCell: (params) => (
          <span style={{ color: "#374151", fontSize: "13px" }}>
            {params.value}
          </span>
        ),
      },
      {
        field: "project_cost",
        headerName: "Project cost",
        flex: 1.1,
        minWidth: 120,
        headerAlign: "right",
        align: "right",
        renderCell: (params) => (
          <span style={{ color: "#374151", fontSize: "13px" }}>
            {params.value}
          </span>
        ),
      },
      {
        field: "share",
        headerName: "Share",
        flex: 0.8,
        minWidth: 90,
        headerAlign: "right",
        align: "right",
        renderCell: (params) => (
          <span style={{ color: "#64748b", fontSize: "13px" }}>
            {params.value}
          </span>
        ),
      },
      {
        field: "earning",
        headerName: "Provisional earning",
        flex: 1.3,
        minWidth: 130,
        headerAlign: "right",
        align: "right",
        renderCell: (params) => (
          <span
            style={{
              color: params.value === "withheld" ? "#9ca3af" : "#16a34a",
              fontWeight: 500,
              fontSize: "13px",
            }}
          >
            {params.value}
          </span>
        ),
      },
      {
        field: "status",
        headerName: "Basis",
        flex: 1.0,
        minWidth: 110,
        renderCell: (params) => {
          if (params.value === "Too early") {
            return (
              <span className="badge-status-too-early">
                Too early
              </span>
            );
          }
          return (
            <span className="badge-status-provisional">
              {params.value || "Provisional"}
            </span>
          );
        },
      },
    ],
    []
  );

  const totals = useMemo(() => {
    let totalHours = 0;
    let totalCost = 0;
    let totalProjectCost = 0;
    let totalEarning = 0;

    if (Array.isArray(inProgress)) {
      inProgress.forEach((p) => {
        totalHours += Number(p.working_hours || 0);
        totalCost += Number(p.cost || 0);
        totalProjectCost += Number(p.project_cost || p.cost || 0);
        totalEarning += Number(p.provisional_earning || p.received_amount || 0);
      });
    }

    return {
      hours: totalHours % 1 === 0 ? String(totalHours) : totalHours.toFixed(2),
      cost: totalCost.toLocaleString("en-IN", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }),
      project_cost: totalProjectCost.toLocaleString("en-IN", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }),
      earning: totalEarning.toLocaleString("en-IN", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }),
    };
  }, [inProgress]);

  const rows = useMemo(() => {
    if (!selectedUserId || !Array.isArray(inProgress) || inProgress.length === 0)
      return [];

    return inProgress.map((item, idx) => {
      const shareVal = item.share != null ? `${item.share}%` : "—";
      const modelFmt =
        item.model === "CONTRACT"
          ? "Contract"
          : item.model === "FIXED_COST"
          ? "Fixed Cost"
          : item.model === "TIME_AND_MATERIAL"
          ? "T&M(Contract)"
          : item.model || "Fixed Cost";
      return {
        id: `inprog-${item.project_id || idx}`,
        project: item.project || "—",
        model: modelFmt,
        budget_type: item.budget_type || item.model,
        hours: Number(item.working_hours || 0).toFixed(2),
        cost: Number(item.cost || 0).toLocaleString("en-IN", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }),
        project_cost: Number(item.project_cost || item.cost || 0).toLocaleString(
          "en-IN",
          {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          }
        ),
        share: shareVal,
        earning: Number(
          item.provisional_earning || item.received_amount || 0
        ).toLocaleString("en-IN", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }),
        status: item.status || "Provisional",
      };
    });
  }, [inProgress, selectedUserId]);

  const isScrollable = rows.length > 10;

  const InProgressNoRowsOverlay = () => {
    if (!selectedUserId) {
      return (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: "100%",
            fontSize: "16px",
            fontWeight: "bold",
            backgroundColor: isDarkMode ? "#272626" : "#f0f0f0",
            color: isDarkMode ? "white" : "#888",
          }}
        >
          Select resource and view in-progress projects
        </Box>
      );
    }
    return <CustomNoRowsOverlay />;
  };

  return (
    <div className={`card border rounded shadow-sm mb-4 ${isDarkMode ? "bg-dark border-secondary" : ""}`}>
      <div className={`card-header ${isDarkMode ? "bg-dark text-white border-secondary" : "bg-white"} py-3 border-bottom d-flex align-items-center justify-content-between`}>
        <div className="d-flex align-items-center gap-2">
          <span
            className="rounded-circle"
            style={{
              width: "12px",
              height: "12px",
              backgroundColor: "rgb(4, 161, 137)",
            }}
          ></span>
          <span className="fw-semibold" style={{ fontSize: "14px" }}>
            In progress — provisional, computed from timesheets
          </span>
          {selectedUserId && rows.length > 0 && (
            <span className="text-muted small ms-2">
              {rows.length} {rows.length === 1 ? "project" : "projects"}
            </span>
          )}
        </div>
      </div>

      <Box
        sx={{
          width: "100%",
          backgroundColor: isDarkMode ? "#1e1e1e" : "#fff",
          height: isScrollable ? 584 : "auto",
          overflow: "hidden",
        }}
      >
        <DataGrid
          rows={isLoading ? [] : rows}
          columns={columns}
          loading={isLoading}
          autoHeight={!isScrollable}
          hideFooterPagination
          disableRowSelectionOnClick
          getRowId={(row) => row.id}
          rowHeight={48}
          slots={{
            footer: InProgressStickyFooter,
            noRowsOverlay: InProgressNoRowsOverlay,
          }}
          slotProps={{
            footer: {
              totals,
              hasData: Boolean(selectedUserId && !isLoading && rows.length > 0),
            },
          }}
          sx={commonDataGridSx}
        />
      </Box>
    </div>
  );
};

export default InProgressProvisional;
